import type { Store } from './store';
import type { Event, State } from './types';

function isModalState(s: State): boolean {
  return s.kind === 'lightbox' || s.kind === 'pano';
}

// Wire the browser/hardware Back button to close modal overlays (lightbox,
// pano) instead of unloading the SPA. Without this, pressing Back on a phone
// while a photo or 360° is open navigates the history entry that loaded the
// app, which restarts the whole tour from the intro (or hangs the reload).
//
// Mechanism: push one throwaway history entry when a modal opens. A Back press
// pops it and fires `popstate`, which we turn into a CLOSE_* dispatch. Closing
// through the UI (× / Esc / backdrop) instead leaves the state machine, so we
// call history.back() once to keep the entry count balanced — the resulting
// popstate is a no-op because the state is no longer a modal.
export function installHistoryNav(
  store: Store<State>,
  dispatch: (e: Event) => void
): () => void {
  let modalEntryPushed = false;

  const onPopState = () => {
    const s = store.get();
    // Clear the flag BEFORE dispatching: the close transition synchronously
    // notifies the store subscriber below, which must not then call
    // history.back() (the entry is already gone — the browser just popped it).
    if (s.kind === 'lightbox') {
      modalEntryPushed = false;
      dispatch({ type: 'CLOSE_LIGHTBOX' });
    } else if (s.kind === 'pano') {
      modalEntryPushed = false;
      dispatch({ type: 'CLOSE_PANO' });
    }
  };

  const unsubscribe = store.subscribe((s) => {
    const modal = isModalState(s);
    if (modal && !modalEntryPushed) {
      // Same URL (no third arg) so ?profile/?debug query params are preserved.
      history.pushState({ __gardenModal: true }, '');
      modalEntryPushed = true;
    } else if (!modal && modalEntryPushed) {
      modalEntryPushed = false;
      history.back();
    }
  });

  window.addEventListener('popstate', onPopState);

  return () => {
    window.removeEventListener('popstate', onPopState);
    unsubscribe();
  };
}
